/**
 * RealtimeProvider - 实时行情上下文
 * 
 * 提供统一的实时行情数据获取：
 * - 单只股票实时行情
 * - 批量股票实时行情
 * - 智能刷新策略
 */

import { createContext, useContext, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { useTradingSession } from './TradingSessionProvider';
import type { RealtimeQuoteData, BatchQuotesData } from './types';

interface RealtimeContextValue {
  getQuote: (symbol: string) => Promise<RealtimeQuoteData>;
  invalidateQuote: (symbol: string) => void;
  invalidateAll: () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const getQuote = useCallback(async (symbol: string): Promise<RealtimeQuoteData> => {
    const { data } = await apiClient.getQuote(symbol, true);
    return transformQuoteData(data);
  }, []);

  const invalidateQuote = useCallback((symbol: string) => {
    queryClient.invalidateQueries({ queryKey: ['realtime-quote', symbol] });
  }, [queryClient]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['realtime-quote'] });
  }, [queryClient]);

  return (
    <RealtimeContext.Provider
      value={{
        getQuote,
        invalidateQuote,
        invalidateAll,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtimeContext(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtimeContext must be used within RealtimeProvider');
  }
  return context;
}

/**
 * Hook: 获取单只股票实时行情
 */
export function useRealtimeQuote(symbol: string, options?: { enabled?: boolean }) {
  const { session } = useTradingSession();
  const context = useContext(RealtimeContext);

  return useQuery({
    queryKey: ['realtime-quote', symbol],
    queryFn: async () => {
      if (context) {
        return context.getQuote(symbol);
      }
      const { data } = await apiClient.getQuote(symbol, true);
      return transformQuoteData(data);
    },
    enabled: !!symbol && (options?.enabled !== false),
    refetchInterval: session.refreshInterval,
    staleTime: session.isTradingHours ? 2000 : 30000,
    refetchOnWindowFocus: session.isTradingHours,
    refetchOnReconnect: true,
  });
}

/**
 * Hook: 批量获取实时行情
 */
export function useBatchRealtimeQuotes(symbols: string[], options?: { enabled?: boolean }) {
  const { session } = useTradingSession();

  return useQuery({
    queryKey: ['batch-realtime-quotes', symbols.sort().join(',')],
    queryFn: async () => {
      if (!symbols.length) return {} as BatchQuotesData;

      try {
        if (session.isTradingHours) {
          const { data } = await apiClient.getBatchRealtimeQuotes(symbols);
          const result: BatchQuotesData = {};
          for (const [key, value] of Object.entries(data)) {
            result[key] = transformQuoteData(value);
          }
          return result;
        } else {
          // 非交易时段逐个获取
          const result: BatchQuotesData = {};
          for (const symbol of symbols) {
            try {
              const { data } = await apiClient.getQuote(symbol);
              result[symbol] = transformQuoteData(data);
            } catch (error) {
              console.warn(`Failed to get quote for ${symbol}:`, error);
            }
          }
          return result;
        }
      } catch (error) {
        console.warn('Batch realtime quotes failed:', error);
        return {} as BatchQuotesData;
      }
    },
    enabled: symbols.length > 0 && (options?.enabled !== false),
    refetchInterval: session.refreshInterval,
    staleTime: session.isTradingHours ? 3000 : 30000,
    refetchOnWindowFocus: session.isTradingHours,
    refetchOnReconnect: true,
  });
}

/**
 * 转换API数据为统一格式
 */
function transformQuoteData(data: unknown): RealtimeQuoteData {
  const d = data as Record<string, unknown>;
  return {
    symbol: String(d.symbol || ''),
    name: d.name as string | undefined,
    price: Number(d.close || d.price || 0),
    open: Number(d.open || 0),
    high: Number(d.high || 0),
    low: Number(d.low || 0),
    close: Number(d.close || 0),
    volume: Number(d.volume || 0),
    amount: d.amount ? Number(d.amount) : undefined,
    change: Number(d.change || 0),
    changePercent: Number(d.change_percent || d.changePercent || d.pct_change || 0),
    tradeDate: d.trade_date as string | undefined,
    timestamp: d.timestamp as string | undefined,
    isRealtime: Boolean(d.is_realtime || d.isRealtime),
    dataSource: String(d.data_source || d.dataSource || 'unknown'),
  };
}
